import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiUser } from "react-icons/fi";
import { ProfileBody } from "../../../pages/Profile";

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : false,
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mediaQuery.matches);
    update();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    }

    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  return isDesktop;
};

const UserProfileDesktopView = ({ t, onBack }) => (
  <div className="mx-auto w-full max-w-5xl space-y-5 text-left motion-safe:animate-[lumenPortalScreenSlideIn_420ms_cubic-bezier(0.22,1,0.36,1)_both]">
    <header className="flex items-center gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-emerald-100 bg-white/80 text-emerald-800 shadow-sm transition hover:-translate-x-0.5 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100 dark:border-emerald-900/60 dark:bg-slate-950/40 dark:text-emerald-200 dark:hover:bg-emerald-950/30 dark:focus-visible:ring-emerald-900/30"
        aria-label={t("common.back", { defaultValue: "Back" })}
      >
        <FiArrowLeft className="h-5 w-5" aria-hidden="true" />
      </button>

      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm" aria-hidden="true">
        <FiUser className="h-5 w-5" />
      </span>

      <span className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
          {t("parentPortal.profile.eyebrow", { defaultValue: "Account" })}
        </p>
        <h1 className="mt-1 truncate text-3xl font-black tracking-tight text-slate-950 dark:text-slate-50">
          {t("profile.title", { defaultValue: "Profile" })}
        </h1>
        <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
          {t("parentPortal.profile.subtitle", { defaultValue: "Account and notification settings" })}
        </p>
      </span>
    </header>

    <ProfileBody variant="parentDesktop" />

    <style>{`
      @keyframes lumenPortalScreenSlideIn {
        from { opacity: 0; transform: translateX(32px); }
        to { opacity: 1; transform: translateX(0); }
      }
    `}</style>
  </div>
);

const UserProfileMobileView = ({ t, onBack }) => (
  <div className="pp-profile-app-view">
    <div className="pp-profile-topbar">
      <button type="button" onClick={onBack} className="pp-profile-back-button">
        <FiArrowLeft aria-hidden="true" />
        {t("common.back", { defaultValue: "Back" })}
      </button>

      <div className="pp-profile-title-block">
        <span className="pp-profile-icon" aria-hidden="true">
          <FiUser />
        </span>
        <div>
          <h1>{t("profile.title", { defaultValue: "Profile" })}</h1>
          <p>{t("parentPortal.profile.subtitle", { defaultValue: "Account and notification settings" })}</p>
        </div>
      </div>
    </div>

    <ProfileBody variant="parent" />
  </div>
);

const UserProfileView = ({ t, onBack }) => {
  const isDesktop = useIsDesktop();
  return isDesktop ? (
    <UserProfileDesktopView t={t} onBack={onBack} />
  ) : (
    <UserProfileMobileView t={t} onBack={onBack} />
  );
};

export default UserProfileView;
